import { useMemo } from "react";
import type {
	CursorData,
	ViewTagDataSet,
	ViewTagsMap,
} from "../models/TrendingModelsV2";
import { findLastIndexWhere } from "../utils/binarySearch";
import {
	type BucketStat,
	type RangeStats,
	accumulateFromValues,
	accumulateRangeStats,
	readLastRealValue,
} from "../utils/bucketStats";
import { useDebouncedValue } from "./useDebouncedValue";

const SETTLE_DELAY_MS = 150;

const toTime = (timestamp: string | number): number =>
	typeof timestamp === "string" ? new Date(timestamp).getTime() : timestamp;

interface TagEndpoints {
	y1: number;
	y2: number;
	p1Time: number;
	p2Time: number;
}

interface TagAggregates {
	avg: number;
	count: number;
	pmin: { x: number; y: number };
	pmax: { x: number; y: number };
	hasValuesBetween: boolean;
}

/**
 * Hook to calculate cursor data (Y1, Y2, Y2-Y1, min/max between cursors)
 * Uses "last value before cursor" strategy to avoid interpolation issues with sparse data
 *
 * Y1/Y2 track the cursors live via binary search. The range aggregates (avg, min, max)
 * scan every point between the cursors, so they run on debounced cursor positions and
 * settle once the drag or zoom gesture stops.
 */
export const useCursorData = (
	cursor1Time: number,
	cursor2Time: number,
	series: ViewTagDataSet[][] | undefined,
	viewTags: ViewTagsMap,
	seriesBucketStats?: Record<number, BucketStat[]>,
): CursorData | null => {
	const settledCursor1Time = useDebouncedValue(cursor1Time, SETTLE_DELAY_MS);
	const settledCursor2Time = useDebouncedValue(cursor2Time, SETTLE_DELAY_MS);

	// Sorted DESCENDING to match the order series data arrives in
	const orderedTags = useMemo(
		() =>
			Object.values(viewTags)
				.filter((tag) => tag && tag.viewTag)
				.sort((a, b) => b.order - a.order),
		[viewTags],
	);

	const endpoints = useMemo(() => {
		const byTagId: Record<number, TagEndpoints> = {};
		if (!series) return byTagId;

		orderedTags.forEach((tagInfo, index) => {
			const tagId = tagInfo.viewTag.TagId;
			const tagData = series[index];

			if (!tagData || tagData.length === 0) {
				byTagId[tagId] = { y1: 0, y2: 0, p1Time: 0, p2Time: 0 };
				return;
			}

			const idx1 = findLastIndexWhere(
				tagData,
				(point) => toTime(point.timestamp) <= cursor1Time,
			);
			const idx2 = findLastIndexWhere(
				tagData,
				(point) => toTime(point.timestamp) <= cursor2Time,
			);
			const lastIdx = idx2 > idx1 ? idx2 : idx1;
			const stats = seriesBucketStats?.[tagId];

			byTagId[tagId] = {
				y1:
					idx1 >= 0
						? readLastRealValue(stats, idx1, tagData[idx1].value ?? 0)
						: 0,
				y2:
					lastIdx >= 0
						? readLastRealValue(stats, lastIdx, tagData[lastIdx].value ?? 0)
						: 0,
				p1Time: idx1 >= 0 ? toTime(tagData[idx1].timestamp) : 0,
				p2Time: lastIdx >= 0 ? toTime(tagData[lastIdx].timestamp) : 0,
			};
		});

		return byTagId;
	}, [orderedTags, series, seriesBucketStats, cursor1Time, cursor2Time]);

	const aggregates = useMemo(() => {
		const byTagId: Record<number, TagAggregates> = {};
		if (!series) return byTagId;

		orderedTags.forEach((tagInfo, index) => {
			const tagId = tagInfo.viewTag.TagId;
			const tagData = series[index];

			if (!tagData || tagData.length === 0) {
				byTagId[tagId] = {
					avg: 0,
					count: 0,
					pmin: { x: 0, y: 0 },
					pmax: { x: 0, y: 0 },
					hasValuesBetween: true,
				};
				return;
			}

			const idx1 = findLastIndexWhere(
				tagData,
				(point) => toTime(point.timestamp) <= settledCursor1Time,
			);
			const idx2 = findLastIndexWhere(
				tagData,
				(point) => toTime(point.timestamp) <= settledCursor2Time,
			);

			const stats = seriesBucketStats?.[tagId];
			const ranged: RangeStats = stats
				? accumulateRangeStats(stats, idx1 + 1, idx2)
				: accumulateFromValues(tagData, idx1 + 1, idx2);

			byTagId[tagId] = {
				avg: ranged.avg,
				count: ranged.count,
				pmin: ranged.pmin,
				pmax: ranged.pmax,
				hasValuesBetween: ranged.hasValues,
			};
		});

		return byTagId;
	}, [
		orderedTags,
		series,
		seriesBucketStats,
		settledCursor1Time,
		settledCursor2Time,
	]);

	return useMemo(() => {
		if (!series || series.length === 0 || Object.keys(viewTags).length === 0) {
			return null;
		}

		const tagValues: CursorData["tagValues"] = {};

		orderedTags.forEach((tagInfo) => {
			const tagId = tagInfo.viewTag.TagId;
			const tagEndpoints = endpoints[tagId];
			const tagAggregates = aggregates[tagId];

			if (!tagEndpoints || !tagAggregates) {
				return;
			}

			const { y1, y2, p1Time, p2Time } = tagEndpoints;

			tagValues[tagId] = {
				y1,
				y2,
				y2y1: y2 - y1,
				avg: tagAggregates.avg,
				count: tagAggregates.count,
				// No data points between cursors - fall back to the endpoint values
				pmin: tagAggregates.hasValuesBetween
					? tagAggregates.pmin
					: { x: p1Time, y: y1 },
				pmax: tagAggregates.hasValuesBetween
					? tagAggregates.pmax
					: { x: p2Time, y: y2 },
			};
		});

		return {
			x1: cursor1Time,
			x2: cursor2Time,
			tagValues,
		};
	}, [
		series,
		viewTags,
		orderedTags,
		endpoints,
		aggregates,
		cursor1Time,
		cursor2Time,
	]);
};
